// The card that opens inside a row (SPEC-redesign §5.4, dossier §4.5). Four
// kinds — vehicle, ZTM notice, event, Aquanet report — and one closing gesture.
//
// It expands inside the row and never replaces the list: a resident who opens a
// tram keeps the rest of their afternoon in view above and below it.
import { linesLabel } from '../shared/alerts.ts';
import { clockLabel, vehicleDetails } from '../shared/format.ts';
import { distanceLabel } from '../shared/geo.ts';
import type { Item } from '../shared/model.ts';
import { moveCentre } from '../state/commands.ts';
import { viewState } from '../state/viewstate.ts';

/** What the card needs from around the item: where we are measuring from, and when. */
export type DetailContext = {
  /** Metres from the named centre, or `null` with no location. */
  metres: number | null;
  /** The axis's „teraz", so the card dates a report from the clock (§4.4). */
  now: number;
  /** The ZTM notice that names this vehicle's line, threaded under it (§5.4). */
  notice: Item | null;
  /** The resident's own lines, so „Usuń z moich linii" knows what to remove. */
  lines: string[];
  /** The line lens, so the vehicle card offers the way back out of it. */
  onlyLine: string | null;
};

const el = <K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text = '',
): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag);
  node.className = className;
  if (text) node.textContent = text;
  return node;
};

/** A stacked full-width button, 44 px: filled ink for the primary, outlined otherwise. */
function action(label: string, primary: boolean, onClick: () => void): HTMLButtonElement {
  const button = el(
    'button',
    `detail__action detail__action--btn${primary ? ' detail__action--primary' : ''}`,
    label,
  );
  button.type = 'button';
  button.addEventListener('click', onClick);
  return button;
}

/**
 * The address of a source, or `null` when it is not one we will put behind a
 * button the resident can press.
 *
 * `Item.url` reaches us from poznan.pl, Aquanet and ztm.poznan.pl by way of a
 * scraper: it is third-party text, and an `href` is the one place in this file
 * where text becomes something the browser will *execute*. `javascript:` and
 * `data:` are the two that matter, and neither is refused by eye — a leading
 * space, mixed case or an encoded colon all slip past a `startsWith` check.
 * `new URL()` with no base is what settles it: it resolves the escapes, so the
 * protocol we test is the one the browser would act on, and a relative or
 * protocol-relative value has no protocol to pass at all and throws.
 *
 * The seam is being made to guarantee the same thing, in the S0 owner's own
 * security PR. This is the second half of it, and the deliberate one: the
 * renderer that trusts its input has no defence left when a guarantee made
 * upstream is one refactor away from being dropped.
 */
export function safeHref(url: string | undefined): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? parsed.href : null;
  } catch {
    // not a URL at all — relative, protocol-relative, or nonsense
    return null;
  }
}

/**
 * A link out to a source: „Więcej na …" leaves the app and says so. Drawn only
 * for an address that survives `safeHref` — a URL guessed from a title, or one
 * the browser would execute rather than follow, is worse than no link at all.
 */
function link(label: string, href: string): HTMLAnchorElement {
  const a = el('a', 'detail__action', label);
  // the property, never an HTML string: nothing here is ever parsed as markup
  a.href = href;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  return a;
}

/** A coordinate we are willing to hand to another site. */
const finite = (n: number, limit: number) => Number.isFinite(n) && Math.abs(n) <= limit;

/** The stacked column every card's buttons live in (dossier §4.5). */
function actionsOf(...items: HTMLElement[]): HTMLElement {
  const box = el('div', 'detail__actions');
  box.append(...items);
  return box;
}

/**
 * Moving the centre is an opening: Back undoes it (§4.2). `at` is written every
 * time, `null` included — „Stąd" on a water fault means here and **now**, and
 * leaving a borrowed 15:00 in place after „Stąd, o 15:00" would move the centre
 * to the fault while the axis went on reading somebody else's hour.
 *
 * Both of those rules are `moveCentre`'s now (SPEC-viewfinder §1.4): a settled
 * pan writes the same thing, and two writers of one state is the duplication
 * #244 exists to end. The behaviour of these two actions is unchanged — only
 * the writer they go through.
 */
function stad(item: Item, name: string, at: string | null) {
  const { position } = item;
  if (!position) return;
  moveCentre(position, at, { name });
}

/** „Tramwaj 16, kierunek jazdy" and the rest, from the fields an `Item` carries. */
function vehicleCard(item: Item, ctx: DetailContext, card: HTMLElement) {
  // `title` is „16 → Os. Sobieskiego"; the card leads with the destination,
  // because that is what the resident is checking.
  const [, headsign = item.title] = item.title.split(' → ');
  const facts = vehicleDetails({
    line: item.lines?.[0] ?? item.title,
    // The seam says which it is, and `| undefined` is there so „it gave none"
    // can be said. Answering that with „bus" would call a tram an Autobus.
    ...(item.mode ? { type: item.mode } : {}),
    headsign,
    delayMin: item.delayMin ?? null,
    // `Item.at` is the fix's own instant (`src/data/data.ts`), so its age is a
    // subtraction, not a second copy of the number.
    fixAgeS: (Date.now() - item.at) / 1000,
    ...(item.nextStop ? { nextStop: item.nextStop } : {}),
  });

  card.append(el('h3', 'detail__heading', facts.headsign));
  card.append(el('p', 'detail__d1', facts.kind));
  const delay = el('p', 'detail__d2', facts.delay);
  delay.dataset.tone = facts.delayState === 'off' ? 'brick' : facts.delayState === 'onTime' ? 'ok' : 'ink';
  card.append(delay);
  if (facts.nextStop) card.append(el('p', 'detail__d3', facts.nextStop));

  // The detour that affects this line, threaded to it with a brick left rule —
  // a resident looking at their tram is told about it here, not three rows away.
  if (ctx.notice) {
    const href = safeHref(ctx.notice.url);
    const block = href ? el('a', 'detail__notice') : el('div', 'detail__notice');
    if (block instanceof HTMLAnchorElement && href) {
      block.href = href;
      block.target = '_blank';
      block.rel = 'noopener noreferrer';
    }
    block.append(
      el('span', 'detail__notice-label mono', 'KOMUNIKAT ZTM'),
      el('p', 'detail__notice-text', ctx.notice.title),
    );
    card.append(block);
  }

  const line = item.lines?.[0];
  if (line) {
    const actions = el('div', 'detail__actions');
    actions.append(
      ctx.onlyLine === line
        ? action('Wszystkie moje linie', true, () => {
            viewState.set({ onlyLine: null }, 'replace');
          })
        : action(`Tylko linia ${line}`, true, () => {
            viewState.set({ onlyLine: line }, 'open');
          }),
    );
    if (ctx.lines.includes(line)) {
      actions.append(
        action('Usuń z moich linii', false, () => {
          viewState.set({ lines: ctx.lines.filter((l) => l !== line) }, 'replace');
        }),
      );
    }
    card.append(actions);
  }
}

function noticeCard(item: Item, card: HTMLElement) {
  card.append(el('h3', 'detail__heading', item.title));
  card.append(el('p', 'detail__d1', `Komunikat ZTM Poznań${linesLabel(item.lines ?? [])}`));
  if (!item.position) card.append(el('p', 'detail__d2', 'Bez lokalizacji na mapie'));
  const notice = safeHref(item.url);
  if (notice) card.append(actionsOf(link('Więcej na ztm.poznan.pl', notice)));
}

function eventCard(item: Item, ctx: DetailContext, card: HTMLElement) {
  const place = item.sub.b;
  card.append(el('h3', 'detail__heading', item.title));
  card.append(el('p', 'detail__d1', place));
  card.append(el('p', 'detail__d2', `${item.sub.a} o ${clockLabel(item.at)}`));
  card.append(el('p', 'detail__d3', ctx.metres == null ? place : `${place}, ${distanceLabel(ctx.metres)}`));
  const actions = el('div', 'detail__actions');
  const more = safeHref(item.url);
  if (more) actions.append(link('Więcej na poznan.pl', more));
  if (item.position) {
    // Built from two numbers we have checked, not by pasting feed text into a
    // URL: the only thing that leaves this app is a latitude and a longitude.
    const { lat, lon } = item.position;
    if (finite(lat, 90) && finite(lon, 180)) {
      const to = new URL('https://www.openstreetmap.org/directions');
      to.searchParams.set('to', `${lat},${lon}`);
      actions.append(link('Prowadź', to.href));
    }
    // „Stąd, o 15:00" moves the centre in time as well as in place: the axis
    // then reads that hour, and ZTM says it cannot follow (ruling 14).
    const at = clockLabel(item.at);
    actions.append(action(`Stąd, o ${at}`, false, () => stad(item, place, at)));
  }
  if (actions.childNodes.length) card.append(actions);
}

/** Aquanet never says when a fault ends, so after two days we only say it is old. */
const OLD_REPORT_MS = 48 * 60 * 60 * 1000;

function reportCard(item: Item, ctx: DetailContext, card: HTMLElement) {
  card.append(el('h3', 'detail__heading', item.title));
  card.append(el('p', 'detail__d1', 'Zgłoszenie Aquanet'));
  card.append(el('p', 'detail__d2', `Zgłoszono ${clockLabel(item.at)}`));
  // From the clock, never from `weight`: `weight` is the dimming scale, and
  // reading a fact back out of the way we drew it is the coupling §4.4 forbids.
  if (ctx.now - item.at > OLD_REPORT_MS) {
    card.append(el('p', 'detail__d3', 'Zgłoszenie starsze niż 48 h'));
  }
  const actions = el('div', 'detail__actions');
  const more = safeHref(item.url);
  if (more) actions.append(link('Więcej na aquanet.pl', more));
  if (item.position) actions.append(action('Stąd', false, () => stad(item, item.title, null)));
  if (actions.childNodes.length) card.append(actions);
}

/**
 * The open card for one item. `‹ Zamknij szczegóły` is `closeDetail()` and
 * never a bare `history.back()`: after „Stąd, o 15:00" that would undo the
 * centre move and leave the card open (§4.2, ruling 6, D-1).
 */
export function renderDetail(item: Item, ctx: DetailContext): HTMLElement {
  const card = el('div', 'detail');
  const close = el('button', 'detail__close mono', '‹ Zamknij szczegóły');
  close.type = 'button';
  close.setAttribute('aria-label', 'Zamknij szczegóły');
  close.addEventListener('click', () => {
    viewState.closeDetail();
  });
  card.append(close);

  if (item.kind === 'vehicle') vehicleCard(item, ctx, card);
  else if (item.kind === 'notice') noticeCard(item, card);
  else if (item.kind === 'event') eventCard(item, ctx, card);
  else reportCard(item, ctx, card);

  return card;
}
